import {
  Activity,
  BarChart3,
  Bell,
  Boxes,
  Building2,
  GitCompare,
  Globe,
  LayoutDashboard,
  type LucideIcon,
  MapPin,
  Network,
  Radio,
  Rocket,
  Route,
  ScrollText,
  Server,
  Settings,
  Share2,
  ShieldCheck,
  Stethoscope,
  Waypoints,
} from 'lucide-react';

/**
 * The single source of truth for primary navigation (Phase 2.1).
 *
 * The sidebar, and later the command palette, breadcrumbs, and search, derive from
 * this typed registry rather than from JSX. Every destination declares the RBAC
 * permission(s) that make it reachable, so the sidebar can hide what a user cannot
 * open instead of letting them navigate into a permission-denied wall.
 *
 * Domains are Level 1; items are Level 2. Resource-scoped, Level-3 navigation lives
 * on the destination pages as tabs (Phase 2.2), not here — the sidebar is not a
 * directory of every page.
 */
export interface NavItem {
  /** Stable id, also the i18n and test hook. */
  id: string;
  /** i18n key under `nav.*`. */
  labelKey: string;
  /** Router path (relative to the app root). */
  route: string;
  icon: LucideIcon;
  /**
   * The item is visible if the user holds ANY of these permissions. Omitted means
   * "any signed-in user". Platform/tenant admins short-circuit in `can()`.
   */
  requiredPermissions?: string[];
  /** Hidden in Basic mode; shown in Advanced mode (lower-level tooling). */
  advanced?: boolean;
  /** 1 = surface first on mobile; higher = less prominent. */
  mobilePriority?: number;
  /** Extra terms for command-palette/search matching (Phase 2.3). */
  searchKeywords?: string[];
  /** Badge provider id (Phase 2.6); resolved live in useBadges(). */
  badge?: string;
}

export interface NavDomain {
  id: string;
  /** i18n key under `nav.groups.*`. */
  labelKey: string;
  icon: LucideIcon;
  order: number;
  items: NavItem[];
  /**
   * Optional domain landing page (Phase 2.7). When set, activating the group header
   * navigates here (and opens the group) instead of only toggling.
   */
  landingRoute?: string;
}

/**
 * Seven domains, populated only with routes that exist today (no empty groups).
 * Security / Platform / Connections are intentionally absent until they have web
 * pages — the taxonomy accommodates them, the sidebar does not render them empty.
 */
export const NAVIGATION: NavDomain[] = [
  {
    id: 'overview',
    labelKey: 'nav.groups.overview',
    icon: LayoutDashboard,
    order: 1,
    items: [
      {
        id: 'dashboard',
        labelKey: 'nav.dashboard',
        route: '/',
        icon: LayoutDashboard,
        mobilePriority: 1,
        searchKeywords: ['home', 'overview', 'summary'],
      },
    ],
  },
  {
    id: 'network',
    labelKey: 'nav.groups.network',
    icon: Network,
    order: 2,
    landingRoute: '/network',
    items: [
      {
        id: 'sites',
        labelKey: 'nav.sites',
        route: '/sites',
        icon: MapPin,
        requiredPermissions: ['site:read'],
        mobilePriority: 2,
        searchKeywords: ['location', 'branch'],
      },
      {
        id: 'devices',
        labelKey: 'nav.devices',
        route: '/devices',
        icon: Server,
        requiredPermissions: ['edge_device:read'],
        mobilePriority: 2,
        searchKeywords: ['edge', 'appliance', 'agent'],
      },
      {
        id: 'relays',
        labelKey: 'nav.relays',
        route: '/relays',
        icon: Radio,
        requiredPermissions: ['relay:read'],
        searchKeywords: ['nat', 'bridge'],
      },
      {
        id: 'address-pools',
        labelKey: 'nav.addressPools',
        route: '/address-pools',
        icon: Boxes,
        requiredPermissions: ['address_pool:read'],
        advanced: true,
        searchKeywords: ['ipam', 'cidr', 'subnet'],
      },
    ],
  },
  {
    id: 'sdwan',
    labelKey: 'nav.groups.sdwan',
    icon: Share2,
    order: 3,
    items: [
      {
        id: 'fabrics',
        labelKey: 'nav.fabrics',
        route: '/fabrics',
        icon: Share2,
        requiredPermissions: ['fabric:read'],
        mobilePriority: 2,
        searchKeywords: ['overlay', 'mesh', 'hub', 'spoke'],
      },
      {
        id: 'topology',
        labelKey: 'nav.topology',
        route: '/topology',
        icon: Waypoints,
        requiredPermissions: ['topology:read'],
        searchKeywords: ['map', 'graph'],
      },
      {
        id: 'wireguard',
        labelKey: 'nav.wireguard',
        route: '/wireguard',
        icon: ShieldCheck,
        searchKeywords: ['tunnel', 'wg', 'peer', 'key'],
      },
    ],
  },
  {
    id: 'routing',
    labelKey: 'nav.groups.routing',
    icon: Route,
    order: 4,
    landingRoute: '/routing',
    items: [
      {
        id: 'route-explorer',
        labelKey: 'nav.routeExplorer',
        route: '/route-explorer',
        icon: Route,
        requiredPermissions: ['routing:read'],
        searchKeywords: ['ospf', 'bgp', 'prefix', 'fib'],
      },
    ],
  },
  {
    id: 'operations',
    labelKey: 'nav.groups.operations',
    icon: Activity,
    order: 5,
    landingRoute: '/operations',
    items: [
      {
        id: 'alerts',
        labelKey: 'nav.alerts',
        route: '/alerts',
        icon: Bell,
        requiredPermissions: ['alert:read'],
        mobilePriority: 1,
        badge: 'alerts',
        searchKeywords: ['incident', 'notification'],
      },
      {
        id: 'deployments',
        labelKey: 'nav.deployments',
        route: '/deployments',
        icon: Rocket,
        requiredPermissions: ['deployment:read'],
        mobilePriority: 2,
        badge: 'deployments',
        searchKeywords: ['rollout', 'apply', 'plan'],
      },
      {
        id: 'drift',
        labelKey: 'nav.drift',
        route: '/drift',
        icon: GitCompare,
        requiredPermissions: ['deployment:read'],
        badge: 'drift',
        searchKeywords: ['reconcile', 'diff'],
      },
      {
        id: 'diagnostics',
        labelKey: 'nav.diagnostics',
        route: '/diagnostics',
        icon: Stethoscope,
        mobilePriority: 1,
        searchKeywords: ['ping', 'trace', 'health', 'probe'],
      },
      {
        id: 'reports',
        labelKey: 'nav.reports',
        route: '/reports',
        icon: BarChart3,
        searchKeywords: ['capacity', 'utilization', 'analytics'],
      },
      {
        id: 'audit',
        labelKey: 'nav.audit',
        route: '/audit',
        icon: ScrollText,
        requiredPermissions: ['audit:read'],
        searchKeywords: ['log', 'history', 'compliance'],
      },
    ],
  },
  {
    id: 'federation',
    labelKey: 'nav.groups.federation',
    icon: Globe,
    order: 6,
    items: [
      {
        id: 'federation',
        labelKey: 'nav.federation',
        route: '/federation',
        icon: Globe,
        requiredPermissions: ['federation:read'],
        badge: 'federation',
        searchKeywords: ['trust', 'interconnect', 'agreement', 'approval'],
      },
    ],
  },
  {
    id: 'administration',
    labelKey: 'nav.groups.administration',
    icon: Settings,
    order: 7,
    items: [
      {
        id: 'organizations',
        labelKey: 'nav.organizations',
        route: '/organizations',
        icon: Building2,
        requiredPermissions: ['organization:read'],
        searchKeywords: ['org', 'tenant'],
      },
      {
        id: 'admin',
        labelKey: 'nav.administration',
        route: '/admin',
        icon: Settings,
        requiredPermissions: ['tenant:admin'],
        searchKeywords: ['settings', 'users', 'roles', 'tokens', 'mfa'],
      },
    ],
  },
];

/** Every destination, flattened — used to resolve favorites and the current page. */
export const ALL_ITEMS: NavItem[] = NAVIGATION.flatMap((domain) => domain.items);

/** The most specific destination matching a pathname (for "pin this page"). */
export function itemForPath(pathname: string): NavItem | undefined {
  return ALL_ITEMS.filter((i) =>
    i.route === '/' ? pathname === '/' : pathname === i.route || pathname.startsWith(`${i.route}/`),
  ).sort((a, b) => b.route.length - a.route.length)[0];
}

